import type { BottleUnit, WeightUnit } from "@babia/checks";
import { useState } from "react";
import {
  draftOf,
  draftProblem,
  type EntryDraft,
  type EntryPatch,
  patchOf,
} from "../../../lib/entry-edit.ts";
import {
  DIAPER_EMOJI,
  DIAPER_KINDS,
  DIAPER_LABEL,
  entryEmoji,
  entryName,
  formatDose,
  TYPE_EMOJI,
} from "../../../lib/entry-text.ts";
import type { Entry, Household } from "../../../lib/queries.ts";
import { cn } from "../../../lib/utils.ts";
import { Button } from "../../ui/button.tsx";
import { Sheet } from "../../ui/sheet.tsx";
import { AmountStepper } from "./bottle.tsx";
import { DoseFields } from "./meds.tsx";
import { NoteFields } from "./note.tsx";
import { TimeField } from "./time-field.tsx";

interface EditSheetProps {
  /** The entry as it was when the sheet opened; null keeps the sheet closed. */
  base: Entry | null;
  /** The entry is still a temporary row: Save waits for the server's. */
  saving: boolean;
  babies: { id: string; name: string }[];
  /** Babies with a nap in progress: an open nap can't move onto one. */
  asleep: ReadonlySet<string>;
  unit: BottleUnit;
  weightUnit: WeightUnit;
  /** The bottle chips and the stepper's start, in `unit`. */
  presets: number[];
  usual: number;
  medPresets: Household["medPresets"];
  now: Date;
  onClose: () => void;
  /** Only the fields that changed; empty when nothing did. */
  onSave: (patch: EntryPatch) => void;
}

/**
 * A parent's fix to a logged entry (#91): its time, its baby, and its own
 * type's fields, filled with the saved values. Save sends only what changed.
 * The caller gives each opening a new `key`, so the draft starts fresh.
 */
export function EditSheet({ base, ...props }: EditSheetProps) {
  return base ? <EditForm base={base} {...props} /> : null;
}

function EditForm({
  base,
  saving,
  babies,
  asleep,
  unit,
  weightUnit,
  presets,
  usual,
  medPresets,
  now,
  onClose,
  onSave,
}: EditSheetProps & { base: Entry }) {
  const units = { bottle: unit, weight: weightUnit };
  const [draft, setDraft] = useState(() => draftOf(base, units));
  const set = <K extends keyof EntryDraft>(key: K, value: EntryDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const problem = draftProblem(base, draft, now);
  const openNap = base.type === "sleep" && !base.endedAt;
  // The latest a time can step to: now, or the saved time if `now` (20 s
  // ticks) lags an entry logged a moment ago, so +5 can always step back.
  const latest = new Date(Math.max(now.getTime(), base.at.getTime(), base.endedAt?.getTime() ?? 0));
  // A medicine no longer in Settings stays a choice.
  const meds =
    base.label && !medPresets.some((m) => m.name === base.label)
      ? [{ name: base.label, dose: null }, ...medPresets]
      : medPresets;

  return (
    <Sheet
      open
      title={`Edit ${entryName({ ...base, noteKind: draft.noteKind }).toLowerCase()}`}
      emoji={entryEmoji({ ...base, noteKind: draft.noteKind })}
      onClose={onClose}
      className="max-h-[92dvh] overflow-y-auto overscroll-contain"
    >
      {babies.length > 1 ? (
        <div role="group" aria-label="Baby" className="mt-3.5 flex flex-wrap gap-2">
          {babies.map((b) => {
            // An open nap can't join a baby who is already asleep.
            const taken = openNap && b.id !== base.babyId && asleep.has(b.id);
            return (
              <Button
                key={b.id}
                variant="chip"
                className={cn("min-w-[30%] px-3", draft.babyId === b.id && "is-selected")}
                aria-pressed={draft.babyId === b.id}
                disabled={taken}
                onClick={() => set("babyId", b.id)}
              >
                <span className="truncate">{b.name}</span>
                {taken ? (
                  <>
                    <span aria-hidden="true">{TYPE_EMOJI.sleep}</span>
                    <span className="sr-only">, asleep</span>
                  </>
                ) : null}
              </Button>
            );
          })}
        </div>
      ) : null}
      {base.type === "sleep" && draft.endedAt ? (
        <>
          <TimeField
            label="Fell asleep"
            value={draft.at}
            max={draft.endedAt}
            now={now}
            onChange={(d) => set("at", d)}
          />
          <TimeField
            label="Woke up"
            value={draft.endedAt}
            min={draft.at}
            max={latest}
            now={now}
            onChange={(d) => set("endedAt", d)}
          />
        </>
      ) : (
        <TimeField
          label={openNap ? "Fell asleep" : "Time"}
          value={draft.at}
          max={latest}
          now={now}
          onChange={(d) => set("at", d)}
        />
      )}
      {openNap ? (
        <p className="mt-2 text-center text-[13px] opacity-65">
          Still asleep. Tap Woke up to end the nap.
        </p>
      ) : null}

      {base.type === "feed" ? (
        <AmountStepper
          unit={unit}
          amount={draft.amount}
          usual={usual}
          presets={presets}
          onChange={(amount) => set("amount", amount)}
        />
      ) : null}
      {base.type === "diaper" ? (
        <div role="group" aria-label="Change" className="mt-4 flex gap-2">
          {DIAPER_KINDS.map((k) => (
            <Button
              key={k}
              variant="chip"
              className={cn("flex-col gap-0.5 py-1.5", draft.diaperKind === k && "is-selected")}
              aria-pressed={draft.diaperKind === k}
              onClick={() => set("diaperKind", k)}
            >
              <span aria-hidden="true" className="text-[16px] leading-none">
                {DIAPER_EMOJI[k]}
              </span>
              {DIAPER_LABEL[k]}
            </Button>
          ))}
        </div>
      ) : null}
      {base.type === "meds" ? (
        <>
          <div role="group" aria-label="Medicine" className="mt-4 grid grid-cols-2 gap-2">
            {meds.map((m) => (
              <Button
                key={m.name}
                variant="chip"
                className={cn(
                  "min-w-0 flex-col gap-0 px-2 py-1.5 text-[14px]",
                  draft.label === m.name && "is-selected",
                )}
                aria-pressed={draft.label === m.name}
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    label: m.name,
                    ...(m.dose ? { doseText: String(m.dose.amount), doseUnit: m.dose.unit } : {}),
                  }))
                }
              >
                <span className="max-w-full truncate">{m.name}</span>
                {m.dose ? (
                  <span className="text-[12px] tabular-nums opacity-70">
                    {formatDose(m.dose.amount, m.dose.unit)}
                  </span>
                ) : null}
              </Button>
            ))}
          </div>
          <DoseFields
            name={draft.label}
            text={draft.doseText}
            unit={draft.doseUnit}
            onText={(text) => set("doseText", text)}
            onUnit={(u) => set("doseUnit", u)}
          />
        </>
      ) : null}
      {base.type === "note" ? (
        <NoteFields
          kind={draft.noteKind}
          onKind={(k) => set("noteKind", k)}
          text={draft.text}
          onText={(text) => set("text", text)}
          weight={draft.weight}
          onWeight={(w) => set("weight", w)}
          weightHint={null}
          // The server keeps a weight note's number and kind together.
          {...(typeof base.weightGrams === "number" ? { lockedKind: "weight" as const } : {})}
          autoFocusText={false}
        />
      ) : null}

      {problem ? (
        <p className="mt-3 text-center text-[13px] text-accent-2" role="status">
          {problem}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="mt-3 w-full"
        disabled={saving || !!problem}
        onClick={() => onSave(patchOf(base, draft, units))}
      >
        {saving ? `Saving the ${entryName(base).toLowerCase()}…` : "Save"}
      </Button>
    </Sheet>
  );
}
